<template>
  <UIDialog :is-open :z-index size="lg" flat @close="handleClose">
    <div ref="containerRef" class="ai-agent-module">
      <UIAIConversation
        ref="conversationRef"
        :loading
        :messages
        :examples
        class="ai-agent-module__conversation"
        @submit="handleSubmit"
      >
        <template #actions>
          <UIButton
            label="Chats"
            variant="gray"
            fill="light"
            size="xs"
            :leading="Bars4Icon"
            @click="handleClickChats"
          />
        </template>
        <template #message="{ message, isLast, handleClickExample }">
          <AIAgentMessage :message :is-last @click:example="handleClickExample" />
        </template>
        <template #messages>
          <UIAIConversationError v-if="errorMessage" :message="errorMessage" @retry="aiAgentStore.retry" />
        </template>
      </UIAIConversation>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, useTemplateRef } from 'vue'

import { ModalClass } from '@types'

import { useAIAgentStore } from '@/store/ai/agent'
import { useModalsStore } from '@/store/modals'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'

import AIAgentMessage from './AIAgentMessage.vue'

import AIAgentConversations from './AIAgentConversations.vue'
import { Bars4Icon } from '@heroicons/vue/24/outline'
import { UIAIConversation, UIAIConversationError } from '@ui/ai'
import { UIButton } from '@ui/buttons'
import { UIDialog } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
  initMessage?: string
}

const props = defineProps<Props>()

const aiAgentStore = useAIAgentStore()
const modalsStore = useModalsStore()
const transactionsSettingsStore = useTransactionsSettingsStore()

const conversationRef = useTemplateRef('conversationRef')

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const loading = computed(() => aiAgentStore.loading)
const messages = computed(() => aiAgentStore.getMessages.filter(message => message.role !== 'system'))

const examples = computed(() => [
  'I bought a house',
  'I took out a loan from the bank',
  'Add private fund',
  'I bought a painting',
  'Add private security',
])

const errorMessage = computed(() => aiAgentStore.getErrorMessage)

const handleSubmit = async (data: string, callback?: () => void) => {
  await aiAgentStore.submit(data)
  callback?.()
}

const handleClickNewChat = () => {
  aiAgentStore.reset(false)
  conversationRef.value?.reset()
}

const handleClose = () => {
  props.modal.close()
}

const handleClickChats = () => {
  const modalInstance = modalsStore.init('ai-agent-conversations', markRaw(AIAgentConversations))
  modalInstance?.open(modalsStore.getZIndex(), {
    onNewChat: handleClickNewChat,
  })
}

onMounted(() => {
  if (!transactionsSettingsStore.initFlag) {
    transactionsSettingsStore.fetch()
  }
  if (props.initMessage) {
    aiAgentStore.submit(props.initMessage)
  }
})
</script>

<style>
.ai-agent-module {
  @apply min-h-96;
  @apply flex flex-auto;
  @apply overflow-hidden;

  &.ui-ai-conversation--started {
    @apply pt-0;
  }

  &__conversation {
    @apply w-full;
    @apply flex flex-col flex-auto;
    @apply px-6;
    @apply overflow-hidden;
  }

  .ui-ai-conversation__area {
    @apply pb-6;
    @apply overflow-y-auto;
  }

  .ui-ai-conversation__header {
    @apply pr-6;
  }
}
</style>
